<!--C:\VUE3\platform\src\components\popup\CouponPopup.vue-->
<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
} from "vue";
import { useDebounceFn } from "@vueuse/core";
import mallApi from "@/common/api/mallApi";
import { useAuthRoles } from "@/composables/useAuthRoles";
import { logDebug, logInfo, logError } from "@/utils/logHelper";

/* ===========================================================================
 * 상수 정의
 * =========================================================================== */
const COUPON_STATUS = Object.freeze({
  AVAILABLE: "A",
  USED: "U",
  EXPIRED: "E",
  CANCELLED: "C",
});

const COUPON_STATUS_LABEL = Object.freeze({
  [COUPON_STATUS.AVAILABLE]: "사용 가능",
  [COUPON_STATUS.USED]: "이미 사용됨",
  [COUPON_STATUS.EXPIRED]: "만료됨",
  [COUPON_STATUS.CANCELLED]: "취소됨",
});

const DISCOUNT_TYPE = Object.freeze({
  FIXED: "F",
  PERCENT: "P",
});

// 비즈니스 에러 코드 매핑
const COUPON_ERROR_MESSAGES = Object.freeze({
  COUPON_ALREADY_REGISTERED: "이미 등록한 쿠폰입니다.",
  COUPON_NOT_FOUND: "유효하지 않은 쿠폰 코드입니다.",
  COUPON_EXPIRED: "만료된 쿠폰입니다.",
  COUPON_LIMIT_EXCEEDED: "쿠폰 발급 한도를 초과했습니다.",
  COUPON_NOT_AVAILABLE: "현재 사용할 수 없는 쿠폰입니다.",
});

// 필터 타입 상수
const FILTER_TYPE = Object.freeze({
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
});

/* ===========================================================================
 * Emits & Instance
 * =========================================================================== */
const emits = defineEmits(["close", "applyCoupon"]);

const { proxy } = getCurrentInstance() || {};
const { userSuid } = useAuthRoles();

/* ===========================================================================
 * 상태 변수
 * =========================================================================== */
const coupons = ref([]);
const selectedCouponId = ref(null);
const couponCode = ref("");
const isLoading = ref(false);
const isRegistering = ref(false);

// 인라인 에러 메시지 상태
const inputError = ref("");

// ✅ 필터 상태 추가
const activeFilter = ref(FILTER_TYPE.AVAILABLE);

/* ===========================================================================
 * Computed
 * =========================================================================== */
const hasCouponCode = computed(() => couponCode.value.trim().length > 0);
const isRegisterDisabled = computed(
  () => !hasCouponCode.value || isRegistering.value
);
const hasInputError = computed(() => inputError.value.length > 0);

// ✅ 사용 가능 쿠폰
const availableCoupons = computed(() =>
  coupons.value.filter((c) => c.couponUseStatus === COUPON_STATUS.AVAILABLE)
);

// ✅ 사용 불가 쿠폰 (사용됨, 만료, 취소)
const unavailableCoupons = computed(() =>
  coupons.value.filter((c) => c.couponUseStatus !== COUPON_STATUS.AVAILABLE)
);

// ✅ 현재 필터에 따른 쿠폰 리스트
const filteredCoupons = computed(() =>
  activeFilter.value === FILTER_TYPE.AVAILABLE
    ? availableCoupons.value
    : unavailableCoupons.value
);

// ✅ 각 탭 개수 (뱃지 표시용)
const availableCount = computed(() => availableCoupons.value.length);
const unavailableCount = computed(() => unavailableCoupons.value.length);

// ✅ 전체 쿠폰 존재 여부
const hasCoupons = computed(() => coupons.value.length > 0);

/* ===========================================================================
 * 유틸리티 함수
 * =========================================================================== */
function getStatusLabel(status) {
  return COUPON_STATUS_LABEL[status] ?? "알 수 없음";
}

function formatDiscount(type, value) {
  if (type === DISCOUNT_TYPE.FIXED) {
    return `${value?.toLocaleString("ko-KR") ?? value}원 할인`;
  }
  if (type === DISCOUNT_TYPE.PERCENT) {
    return `${value}% 할인`;
  }
  return "";
}

function transformCouponData(item) {
  const statusLabel = getStatusLabel(item.couponUseStatus);
  return {
    userCouponId: item.userCouponId,
    userCouponCode: item.userCouponCode,
    couponName: item.couponName || "(이름 없음)",
    discountType: item.discountType,
    discountValue: item.discountValue,
    discountInfo: formatDiscount(item.discountType, item.discountValue),
    couponUseStatus: item.couponUseStatus,
    couponUseStatusLabel: statusLabel,
    description: item.description || "",
    expireAt: item.expireAt ? item.expireAt.slice(0, 10) : "",
    raw: item,
  };
}

// 인라인 에러 초기화
function clearInputError() {
  inputError.value = "";
}

// 에러 메시지 추출 (비즈니스 vs 시스템 분류)
function getErrorMessage(error) {
  const errorCode =
    error?.response?.data?.code ||
    error?.response?.data?.errorCode ||
    error?.errorResponse?.code;

  if (errorCode && COUPON_ERROR_MESSAGES[errorCode]) {
    return {
      message: COUPON_ERROR_MESSAGES[errorCode],
      isBusiness: true,
    };
  }

  const serverMessage =
    error?.response?.data?.message || error?.errorResponse?.message;

  if (serverMessage) {
    return { message: serverMessage, isBusiness: true };
  }

  return {
    message: "쿠폰 등록 중 오류가 발생했습니다.",
    isBusiness: false,
  };
}

/* ===========================================================================
 * API 호출
 * =========================================================================== */
async function fetchCoupons() {
  if (!userSuid.value) return;

  isLoading.value = true;
  try {
    logDebug("쿠폰 조회 API 호출 시작");
    const response = await mallApi.getCouponById({
      userSuid: userSuid.value,
    });

    if (response?.userCouponsList && Array.isArray(response.userCouponsList)) {
      coupons.value = response.userCouponsList.map(transformCouponData);
      logInfo(`쿠폰 ${coupons.value.length}개 조회 완료`);
    } else {
      coupons.value = [];
      logDebug("받아온 쿠폰 목록이 비어있습니다.");
    }
  } catch (error) {
    logError("쿠폰 조회 중 오류 발생", error);
    proxy?.$toast?.error("쿠폰 조회 중 오류가 발생했습니다.");
    coupons.value = [];
  } finally {
    isLoading.value = false;
  }
}

/* ===========================================================================
 * 이벤트 핸들러
 * =========================================================================== */

// ✅ 필터 변경
function setFilter(filter) {
  activeFilter.value = filter;
}

const handleRegisterCoupon = useDebounceFn(async () => {
  clearInputError();

  if (!hasCouponCode.value) {
    inputError.value = "쿠폰 번호를 입력해 주세요.";
    return;
  }

  if (!userSuid.value) {
    proxy?.$toast?.warning("로그인이 필요합니다.");
    return;
  }

  const promotionCode = couponCode.value.trim().toUpperCase();
  isRegistering.value = true;

  try {
    const response = await mallApi.registerPromotionCoupon({
      userSuid: userSuid.value,
      promotionCode,
    });

    // Case 1: 비즈니스 실패 (HTTP 200, success: false)
    if (response?.success === false) {
      inputError.value = response.message || "쿠폰 등록에 실패했습니다.";
      return;
    }

    // Case 2: 성공 (HTTP 200, success: true)
    const couponName = response?.content?.couponName || "쿠폰";
    proxy?.$toast?.success(`${couponName}이(가) 등록되었습니다.`);
    couponCode.value = "";

    // ✅ 등록 후 사용 가능 탭으로 전환
    activeFilter.value = FILTER_TYPE.AVAILABLE;
    await fetchCoupons();
  } catch (error) {
    // Case 3: 시스템 에러 (HTTP 4xx/5xx, 네트워크 오류)
    logError("쿠폰 등록 실패:", error);
    proxy?.$toast?.error("쿠폰 등록 중 오류가 발생했습니다.");
  } finally {
    isRegistering.value = false;
  }
}, 300);

// ✅ 쿠폰 선택 (index 대신 coupon 객체 직접 사용)
function selectCoupon(coupon) {
  if (coupon.couponUseStatus !== COUPON_STATUS.AVAILABLE) {
    proxy?.$toast?.info("이 쿠폰은 사용하실 수 없습니다.");
    return;
  }
  selectedCouponId.value = coupon.userCouponId;
  emits("applyCoupon", coupon);
  emits("close");
}

// ✅ 선택 여부 확인
function isSelected(coupon) {
  return selectedCouponId.value === coupon.userCouponId;
}

function onClose() {
  emits("close");
}

function onEscClose(e) {
  if (e.key === "Escape") onClose();
}

/* ===========================================================================
 * 라이프사이클
 * =========================================================================== */
onMounted(() => {
  fetchCoupons();
  document.addEventListener("keydown", onEscClose);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onEscClose);
});
</script>

<template>
  <!-- Bootstrap 수동 모달 -->
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @click.self="onClose"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content shadow">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title mb-0">쿠폰 목록</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="onClose"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- 쿠폰 코드 입력/등록 -->
          <div class="bg-body-tertiary rounded p-3 mb-3">
            <div class="input-group">
              <input
                v-model.trim="couponCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': hasInputError }"
                placeholder="쿠폰 번호 입력 (예: WELCOME2025)"
                maxlength="50"
                :disabled="isRegistering"
                @input="clearInputError"
                @keydown.enter.prevent="handleRegisterCoupon"
              />
              <button
                class="btn btn-primary"
                type="button"
                :disabled="isRegisterDisabled"
                @click="handleRegisterCoupon"
              >
                <span
                  v-if="isRegistering"
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ isRegistering ? "등록 중..." : "쿠폰 확인" }}
              </button>
            </div>

            <!-- 인라인 에러 메시지 -->
            <div
              v-if="hasInputError"
              class="invalid-feedback d-block mt-2"
              role="alert"
            >
              <i class="bi bi-exclamation-circle me-1"></i>
              {{ inputError }}
            </div>

            <p v-else class="small text-body-secondary mb-0 mt-2">
              Mpnite에서 발급된 프로모션 쿠폰 코드를 입력해 주세요.
            </p>
          </div>

          <!-- 로딩 상태 -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">로딩 중...</span>
            </div>
          </div>

          <template v-else>
            <!-- ✅ 필터 버튼 그룹 -->
            <div v-if="hasCoupons" class="d-flex align-items-center gap-2 mb-3">
              <button
                type="button"
                class="btn btn-sm"
                :class="
                  activeFilter === FILTER_TYPE.AVAILABLE
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                "
                @click="setFilter(FILTER_TYPE.AVAILABLE)"
              >
                사용 가능 쿠폰
                <span
                  class="badge ms-1"
                  :class="
                    activeFilter === FILTER_TYPE.AVAILABLE
                      ? 'bg-white text-primary'
                      : 'bg-secondary'
                  "
                >
                  {{ availableCount }}
                </span>
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="
                  activeFilter === FILTER_TYPE.UNAVAILABLE
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                "
                @click="setFilter(FILTER_TYPE.UNAVAILABLE)"
              >
                사용 완료 / 기간 만료
                <span
                  class="badge ms-1"
                  :class="
                    activeFilter === FILTER_TYPE.UNAVAILABLE
                      ? 'bg-white text-primary'
                      : 'bg-secondary'
                  "
                >
                  {{ unavailableCount }}
                </span>
              </button>
            </div>

            <!-- ✅ 쿠폰 리스트 (filteredCoupons 사용) -->
            <div
              v-if="filteredCoupons.length > 0"
              class="row row-cols-1 row-cols-md-2 g-3"
            >
              <div
                v-for="coupon in filteredCoupons"
                :key="coupon.userCouponId"
                class="col"
              >
                <div
                  class="card h-100"
                  :class="{
                    'border-primary': isSelected(coupon),
                    'opacity-75':
                      coupon.couponUseStatus !== COUPON_STATUS.AVAILABLE,
                  }"
                >
                  <div class="card-body d-flex flex-column">
                    <div
                      class="d-flex align-items-start justify-content-between gap-2"
                    >
                      <div>
                        <h3 class="h5 mb-1">{{ coupon.userCouponCode }}</h3>
                        <div class="text-body-secondary small">
                          {{ coupon.couponName }}
                        </div>
                      </div>
                      <span
                        class="badge"
                        :class="{
                          'bg-success':
                            coupon.couponUseStatus === COUPON_STATUS.AVAILABLE,
                          'bg-warning text-dark':
                            coupon.couponUseStatus === COUPON_STATUS.USED,
                          'bg-secondary':
                            coupon.couponUseStatus === COUPON_STATUS.EXPIRED ||
                            coupon.couponUseStatus === COUPON_STATUS.CANCELLED,
                        }"
                      >
                        {{ coupon.couponUseStatusLabel }}
                      </span>
                    </div>

                    <p
                      v-if="coupon.description"
                      class="small text-body-secondary mt-3 mb-2"
                    >
                      {{ coupon.description }}
                    </p>

                    <div class="small mt-auto">
                      <div class="fw-semibold text-primary">
                        {{ coupon.discountInfo }}
                      </div>
                      <div
                        v-if="coupon.expireAt"
                        class="text-body-secondary mt-1"
                      >
                        만료일: {{ coupon.expireAt }}
                      </div>
                    </div>

                    <div class="pt-3">
                      <button
                        type="button"
                        class="btn"
                        :class="[
                          coupon.couponUseStatus === COUPON_STATUS.AVAILABLE
                            ? isSelected(coupon)
                              ? 'btn-success'
                              : 'btn-outline-primary'
                            : 'btn-outline-secondary',
                        ]"
                        :disabled="
                          coupon.couponUseStatus !== COUPON_STATUS.AVAILABLE
                        "
                        @click="selectCoupon(coupon)"
                      >
                        {{
                          coupon.couponUseStatus === COUPON_STATUS.AVAILABLE
                            ? isSelected(coupon)
                              ? "Selected"
                              : "Select"
                            : "사용 불가"
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ✅ 필터된 결과 없음 -->
            <div v-else-if="hasCoupons" class="text-center py-5">
              <i
                class="bi bi-ticket-perforated text-secondary"
                style="font-size: 3rem"
              ></i>
              <p class="mb-0 mt-3 text-secondary">
                {{
                  activeFilter === FILTER_TYPE.AVAILABLE
                    ? "사용 가능한 쿠폰이 없습니다"
                    : "사용 완료/만료된 쿠폰이 없습니다"
                }}
              </p>
            </div>

            <!-- ✅ 전체 쿠폰 없음 -->
            <div v-else class="text-center py-5">
              <i
                class="bi bi-ticket-perforated text-secondary"
                style="font-size: 3rem"
              ></i>
              <p class="mb-0 mt-3 text-secondary">등록된 쿠폰이 없습니다</p>
              <p class="small text-body-secondary mt-2">
                위 입력란에 쿠폰 코드를 입력하여 등록해 주세요.
              </p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <!-- 수동 백드롭 -->
  <div class="modal-backdrop fade show"></div>
</template>

<style scoped>
/* Bootstrap 컴포넌트/유틸리티만 사용 */
</style>
